// Address on the Field Agent wire. It is the one standard record_update change whose value is an
// OBJECT rather than a scalar: BE folds an edit to any sub-field into a single `address` change
// carrying the complete component set on both sides, so the apply can expand it back into the
// individual columns and the platform re-geocodes the whole address (mmc-server
// server/fieldAgent/features/record-update/address-change.ts). Every other change on that action is
// a bare string, number or date, which is why the rest of the card formats and compares by value.

import Address from "@mapmycustomers/shared/types/Address";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";

// The change entry's `field`, which is also the street-line column's own name.
export const ADDRESS_CHANGE_KEY = "address";

// BE's own ADDRESS_COMPONENT_FIELDS, in its own order. Not derived from the field model: these are
// wire keys, and the model has no single field that enumerates them. Shared with create_record,
// which sends the same seven columns FLAT rather than folded into one object.
export const ADDRESS_COLUMNS = [
  "address",
  "city",
  "region",
  "regionCode",
  "postalCode",
  "country",
  "countryCode",
] as const;

// The wire's compound value. `unknown` on the way in because it is `unknown` on the wire — an
// address change that is not an object is a contract break, and the caller falls back to the
// scalar path rather than rendering nothing.
export const isAddressChange = (key: string, value: unknown): value is Record<string, unknown> =>
  key === ADDRESS_CHANGE_KEY && isRecord(value);

// One line, formatted the same way the address control in the edit modal labels its own value, so
// the card and the form cannot disagree about what the rep is confirming.
export const formatAddressComponents = (value: unknown): string =>
  isRecord(value) ? getFormattedAddressForUi(value as Address) : "";

// A conflict's freshValues is keyed by COMPONENT, never by `address` — BE's detectDrift compares the
// live columns one at a time and reports only the ones that moved. So the "before" side is the
// proposal's snapshot with the drifted components laid over it, not a lookup under `address`.
export const addressBefore = (
  currentValue: unknown,
  freshValues: Record<string, unknown> | undefined
): unknown => {
  if (!freshValues || !isRecord(currentValue)) {
    return currentValue;
  }
  const drifted = ADDRESS_COLUMNS.filter((component) => component in freshValues);
  if (!drifted.length) {
    return currentValue;
  }
  return {
    ...currentValue,
    ...Object.fromEntries(drifted.map((component) => [component, freshValues[component]])),
  };
};

// What the rep's edit must look like before it rides in finalValues. The apply reads the override
// component by component and falls back to the PROPOSED value for a key the override omits, so a
// partial object silently keeps whichever parts the rep cleared. The address control emits exactly
// that: picking a new place returns a fresh object with only the components that place has.
//
// Missing and empty both become null — BE's text parser treats null as a clear, and "" is left to
// each write type's own interpretation. Same rule the modal applies to every scalar field.
export const toAddressComponents = (value: unknown): Record<string, null | string> => {
  const source = isRecord(value) ? value : {};
  return Object.fromEntries(
    ADDRESS_COLUMNS.map((component) => {
      const raw = source[component];
      return [component, typeof raw === "string" && raw !== "" ? raw : null];
    })
  );
};
